import { useEffect, useMemo, useState } from 'react'
import {
  BookOpen,
  CheckCircle,
  Circle,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Play,
  Search,
  SlidersHorizontal,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { PageSkeleton } from '@/components/LoadingSkeleton'
import { useAuth } from '@/contexts/AuthContext'
import { UserRole } from '@/models/user.model'
import { paths } from '@/routes/path'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/Modal'
import {
  useCompleteCourseModule,
  useLearnerCourseProgress,
  useLearnerEnrollments,
  useRecordResourceProgress,
} from '@/hooks/useLearnerCourses'
import { LearnerCourseEnrollmentModel } from '@/models/learnerCourse.model'
import { CourseModuleModel, CourseResourceModel } from '@/models/course.model'

type StatusFilter = 'all' | 'in-progress' | 'past'

const CARD_THEMES = [
  {
    card: 'bg-blue-50/80 border-blue-200',
    heading: 'text-blue-700',
    accent: 'text-blue-600',
    progress: 'bg-blue-500',
    avatarBorder: 'border-blue-200',
    avatarIcon: 'text-blue-500',
  },
  {
    card: 'bg-green-50/80 border-green-200',
    heading: 'text-green-700',
    accent: 'text-green-600',
    progress: 'bg-green-500',
    avatarBorder: 'border-green-200',
    avatarIcon: 'text-green-500',
  },
  {
    card: 'bg-purple-50/80 border-purple-200',
    heading: 'text-purple-700',
    accent: 'text-purple-600',
    progress: 'bg-purple-500',
    avatarBorder: 'border-purple-200',
    avatarIcon: 'text-purple-500',
  },
  {
    card: 'bg-pink-50/80 border-pink-200',
    heading: 'text-pink-700',
    accent: 'text-pink-600',
    progress: 'bg-pink-500',
    avatarBorder: 'border-pink-200',
    avatarIcon: 'text-pink-500',
  },
] as const

function formatEnrollmentSubtitle(enrollment: LearnerCourseEnrollmentModel): string {
  if (typeof enrollment.modulesCompleted === 'number' && typeof enrollment.modulesTotal === 'number') {
    return `${enrollment.modulesCompleted} of ${enrollment.modulesTotal} modules completed`
  }
  if (enrollment.courseCode) {
    return `Course code: ${enrollment.courseCode}`
  }
  if (enrollment.enrolledAt) {
    const parsed = new Date(enrollment.enrolledAt)
    if (!Number.isNaN(parsed.getTime())) {
      return `Enrolled ${parsed.toLocaleDateString()}`
    }
  }
  return 'Enrollment details not available'
}

function getEnrollmentProgress(enrollment: LearnerCourseEnrollmentModel): number {
  return Math.min(100, Math.max(0, Math.round(enrollment.progressPercent)))
}

function getEnrollmentStatusLabel(enrollment: LearnerCourseEnrollmentModel): string {
  if (enrollment.isCompleted) {
    return 'Completed'
  }

  const status = enrollment.enrollmentStatus?.toLowerCase()
  switch (status) {
    case 'enrolled':
      return enrollment.progressPercent > 0 ? 'In progress' : 'Not started'
    case 'pending':
      return 'Pending'
    case 'withdrawn':
    case 'dropped':
      return 'Withdrawn'
    case 'suspended':
      return 'Suspended'
    default:
      return enrollment.enrollmentStatus ?? 'Enrolled'
  }
}

function getModuleStatusLabel(module: CourseModuleModel): string {
  const status = module.progress?.status?.toLowerCase()
  if (status === 'completed' || module.progress?.isCompleted) return 'Completed'
  if (status === 'in_progress') return 'In progress'
  return 'Not started'
}

function isResourceCompleted(resource: CourseResourceModel | undefined): boolean {
  return resource?.progress?.completed ?? false
}

export function LearnerCoursesPage() {
  const { user } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [levelFilter, setLevelFilter] = useState<string>('all')
  const [semesterFilter, setSemesterFilter] = useState<string>('all')
  const [selectedEnrollment, setSelectedEnrollment] = useState<LearnerCourseEnrollmentModel | null>(null)
  const [moduleActionId, setModuleActionId] = useState<string | null>(null)
  const [resourceActionId, setResourceActionId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const enrollmentFilters = useMemo(
    () => ({
      level: levelFilter !== 'all' ? Number(levelFilter) : undefined,
      stage: semesterFilter !== 'all' ? Number(semesterFilter) : undefined,
    }),
    [levelFilter, semesterFilter],
  )

  const {
    data: programmeEnrollments,
    isLoading,
    error,
  } = useLearnerEnrollments(enrollmentFilters)

  const {
    data: courseProgress,
    isLoading: isProgressLoading,
    isError: isProgressError,
    error: progressError,
    refetch: refetchCourseProgress,
  } = useLearnerCourseProgress(selectedEnrollment?.courseId, { enabled: Boolean(selectedEnrollment) })

  const recordResourceProgressMutation = useRecordResourceProgress()
  const completeModuleMutation = useCompleteCourseModule()

  const programmes = programmeEnrollments ?? []
  const enrollments = useMemo(
    () =>
      programmes
        .flatMap((programme) => programme.enrollments)
        .filter((enrollment): enrollment is LearnerCourseEnrollmentModel => Boolean(enrollment)),
    [programmes],
  )

  const filteredEnrollments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return enrollments.filter((enrollment) => {
      const matchesSearch =
        query.length === 0 ||
        enrollment.courseTitle.toLowerCase().includes(query) ||
        (enrollment.courseCode ?? '').toLowerCase().includes(query) ||
        enrollment.programmeTitle.toLowerCase().includes(query)

      if (!matchesSearch) return false

      if (levelFilter !== 'all' && enrollment.curriculumYear !== Number(levelFilter)) {
        return false
      }
      if (semesterFilter !== 'all' && enrollment.curriculumSemester !== Number(semesterFilter)) {
        return false
      }

      if (statusFilter === 'all') return true

      const status = enrollment.enrollmentStatus?.toLowerCase()
      const isDropped = status === 'withdrawn' || status === 'dropped'

      if (statusFilter === 'in-progress') {
        return !enrollment.isCompleted && !isDropped
      }

      if (statusFilter === 'past') {
        return enrollment.isCompleted || isDropped
      }

      return true
    })
  }, [enrollments, searchTerm, statusFilter])

  const isInitialLoading = isLoading && programmes.length === 0

  

  const isLearner = user?.role === UserRole.LEARNER

  const handleCloseModal = () => {
    if (recordResourceProgressMutation.isPending || completeModuleMutation.isPending) {
      return
    }
    setSelectedEnrollment(null)
  }

  const handleModuleCompletion = async (module: CourseModuleModel) => {
    if (!selectedEnrollment) return
    setActionError(null)
    setModuleActionId(module.id)
    try {
      await completeModuleMutation.mutateAsync({
        courseId: selectedEnrollment.courseId,
        moduleId: module.id,
        payload: {
          timeSpentSeconds: module.progress?.timeSpentSeconds ?? 0,
        },
      })
      await refetchCourseProgress()
    } catch (mutationError) {
      setActionError((mutationError as Error).message || 'Unable to mark module as completed.')
    } finally {
      setModuleActionId(null)
    }
  }

  const handleResourceCompletion = async (resource: CourseResourceModel) => {
    if (!selectedEnrollment) return
    setActionError(null)
    setResourceActionId(resource.id)
    try {
      await recordResourceProgressMutation.mutateAsync({
        courseId: selectedEnrollment.courseId,
        resourceId: resource.id,
        payload: {
          playedSeconds: resource.progress?.playedSeconds ?? 0,
          completed: true,
        },
      })
      await refetchCourseProgress()
    } catch (mutationError) {
      setActionError((mutationError as Error).message || 'Unable to record resource progress.')
    } finally {
      setResourceActionId(null)
    }
  }

  useEffect(() => {
    if (!selectedEnrollment) {
      setActionError(null)
      setModuleActionId(null)
      setResourceActionId(null)
    }
  }, [selectedEnrollment])

  if (error) {
    return (
      <div className="min-h-screen bg-[#f4f6fb] flex items-center justify-center px-6">
        <div className="max-w-md w-full rounded-3xl bg-white shadow-lg px-8 py-10 text-center space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
            <BookOpen className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Unable to load courses</h2>
          <p className="text-sm text-gray-600">
            {(error as Error)?.message ??
              'Something went wrong while fetching your courses. Please refresh the page or try again later.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-[#f4f6fb] py-10 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Courses</h1>
            <p className="mt-2 text-gray-600">
              {isLearner
                ? 'Continue learning with courses tailored to you.'
                : 'Preview the learner-focused course experience.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm text-gray-600 shadow-sm">
              <SlidersHorizontal className="h-4 w-4 text-gray-500" />
              <label htmlFor="levelFilter" className="text-xs uppercase tracking-wide text-gray-500">
                Year
              </label>
              <select
                id="levelFilter"
                className="border-none bg-transparent text-sm focus:outline-none"
                value={levelFilter}
                onChange={(event) => setLevelFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="1">Year 1</option>
                <option value="2">Year 2</option>
              </select>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm text-gray-600 shadow-sm">
              <Filter className="h-4 w-4 text-gray-500" />
              <label htmlFor="semesterFilter" className="text-xs uppercase tracking-wide text-gray-500">
                Semester
              </label>
              <select
                id="semesterFilter"
                className="border-none bg-transparent text-sm focus:outline-none"
                value={semesterFilter}
                onChange={(event) => setSemesterFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="1">Semester 1</option>
                <option value="2">Semester 2</option>
              </select>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-gray-500 shadow-sm">
              <button
                type="button"
                className="rounded-full p-1.5 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-200"
                aria-label="Grid view"
              >
                <LayoutGrid className="h-5 w-5" />
              </button>
              <span className="h-6 w-px bg-gray-200" aria-hidden="true" />
              <button
                type="button"
                className="rounded-full p-1.5 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-200"
                aria-label="List view"
              >
                <List className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-6 text-sm font-medium text-gray-500">
            <button
              type="button"
              onClick={() => setStatusFilter('all')}
              className={`relative pb-1 transition ${
                statusFilter === 'all' ? 'text-[#7b2a40]' : 'hover:text-gray-700'
              }`}
            >
              All
              {statusFilter === 'all' && (
                <span className="absolute inset-x-0 -bottom-1 h-0.5 rounded-full bg-[#7b2a40]" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('in-progress')}
              className={`relative pb-1 transition ${
                statusFilter === 'in-progress' ? 'text-[#7b2a40]' : 'hover:text-gray-700'
              }`}
            >
              In progress
              {statusFilter === 'in-progress' && (
                <span className="absolute inset-x-0 -bottom-1 h-0.5 rounded-full bg-[#7b2a40]" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('past')}
              className={`relative pb-1 transition ${
                statusFilter === 'past' ? 'text-[#7b2a40]' : 'hover:text-gray-700'
              }`}
            >
              Past
              {statusFilter === 'past' && (
                <span className="absolute inset-x-0 -bottom-1 h-0.5 rounded-full bg-[#7b2a40]" />
              )}
            </button>
          </div>

          <div className="w-full md:w-80">
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search"
              icon={<Search className="h-4 w-4" />}
              rightIcon={<span className="sr-only">Search</span>}
            />
          </div>
        </div>
        {isInitialLoading ? (
          <PageSkeleton content />
        ) : filteredEnrollments.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white px-10 py-16 text-center shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-[#7b2a40]">
              <BookOpen className="h-8 w-8" />
            </div>
            <h3 className="mt-6 text-lg font-semibold text-gray-900">No courses found</h3>
            <p className="mt-2 text-sm text-gray-600">
              {searchTerm
                ? 'Try adjusting your search to find a course.'
                : 'You are not enrolled in any courses yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {filteredEnrollments.map((enrollment, index) => {
              const theme = CARD_THEMES[index % CARD_THEMES.length]
              const progress = getEnrollmentProgress(enrollment)
              const statusLabel = getEnrollmentStatusLabel(enrollment)

              return (
                <article
                  key={enrollment.enrollmentId}
                  className={`flex h-full flex-col justify-between rounded-3xl border px-6 py-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg ${theme.card}`}
                >
                  <div className="space-y-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Link
                          to={paths.app.courses.detail.build(enrollment.courseId)}
                          className={`text-lg font-semibold transition hover:underline ${theme.heading}`}
                        >
                          {enrollment.courseTitle}
                        </Link>
                        <p className="mt-1 text-sm text-gray-500">{enrollment.programmeTitle}</p>
                        <p className="mt-1 text-xs text-gray-500">{formatEnrollmentSubtitle(enrollment)}</p>
                        {(enrollment.curriculumYear || enrollment.curriculumSemester) && (
                          <p className="mt-1 text-xs text-gray-500">
                            Year {enrollment.curriculumYear ?? '—'} • Semester {enrollment.curriculumSemester ?? '—'}
                          </p>
                        )}
                      </div>
                      <div
                        className={`flex h-11 w-11 items-center justify-center rounded-full border bg-white/70 shrink-0 ${theme.avatarBorder}`}
                      >
                        <Play className={`h-5 w-5 ${theme.avatarIcon}`} />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Progress</p>
                      <div className="mt-2 h-2 w-full rounded-full bg-white/70">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ease-out ${theme.progress}`}
                          style={{ width: `${progress}%` }}
                          aria-hidden="true"
                        />
                      </div>
                      <div className="mt-3 flex items-center justify-between text-sm">
                        <span className={`font-medium ${theme.accent}`}>{progress}% Completed</span>
                        <span className="text-gray-500">{statusLabel}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-between gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 border-transparent bg-white/70 text-gray-700 hover:border-primary-200 hover:bg-white"
                      onClick={() => setSelectedEnrollment(enrollment)}
                    >
                      View progress
                    </Button>
                    <Link
                      to={paths.app.courses.content.build(enrollment.courseId)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#7b2a40] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#611f32]"
                    >
                      Continue
                      <Play className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              )
            })}
          </div>
        )}
        </div>
      </div>

      <Modal
        isOpen={Boolean(selectedEnrollment)}
        onClose={handleCloseModal}
        title={selectedEnrollment ? `${selectedEnrollment.courseTitle} progress` : 'Course progress'}
        size="xl"
      >
        {isProgressLoading && (
          <div className="flex items-center justify-center py-8 text-sm text-secondary-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading progress…
          </div>
        )}

        {isProgressError && !isProgressLoading && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {(progressError as Error)?.message ?? 'Unable to load course progress. Please try again later.'}
          </div>
        )}

        {!isProgressLoading && !isProgressError && courseProgress && (
          <div className="space-y-6">
            {actionError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {actionError}
              </div>
            )}

            <div className="rounded-2xl border border-primary-100 bg-primary-50 px-5 py-4">
              <p className="text-sm font-medium text-primary-700">Overall progress</p>
              <p className="mt-2 text-3xl font-semibold text-primary-900">{courseProgress.percentComplete}%</p>
              <p className="mt-1 text-xs text-primary-700">
                {courseProgress.modulesCompleted ?? 0} of {courseProgress.modulesTotal ?? (courseProgress.modules.length || 0)} modules completed
              </p>
            </div>

            {courseProgress.modules.length === 0 ? (
              <div className="rounded-xl border border-dashed border-secondary-200 bg-white px-5 py-6 text-center text-sm text-secondary-500">
                No modules available for this course yet.
              </div>
            ) : (
              <div className="space-y-5">
                {courseProgress.modules.map((module) => {
                  const moduleCompleted = module.progress?.isCompleted ?? false
                  return (
                    <div key={module.id} className="rounded-2xl border border-secondary-100 bg-white px-5 py-4 shadow-sm">
                      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-secondary-400">Module {module.order ?? '—'}</p>
                          <h4 className="text-lg font-semibold text-secondary-900">{module.title}</h4>
                          <p className="mt-1 text-xs text-secondary-500">{getModuleStatusLabel(module)}</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          className={`md:w-auto ${moduleCompleted ? 'border-green-200 bg-green-50 text-green-600' : 'border-secondary-200 text-secondary-600 hover:border-primary-200 hover:text-primary-600'}`}
                          disabled={moduleCompleted || moduleActionId === module.id || completeModuleMutation.isPending}
                          onClick={() => handleModuleCompletion(module)}
                        >
                          {moduleActionId === module.id && completeModuleMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving…
                            </>
                          ) : moduleCompleted ? (
                            <>
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Completed
                            </>
                          ) : (
                            <>
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Mark module completed
                            </>
                          )}
                        </Button>
                      </div>

                      {module.resources.length > 0 && (
                        <div className="mt-4 space-y-3">
                          {module.resources.map((resource) => {
                            const resourceCompleted = isResourceCompleted(resource)
                            return (
                              <div
                                key={resource.id}
                                className="flex flex-col gap-3 rounded-xl border border-secondary-100 bg-secondary-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="flex items-start gap-3">
                                  {resourceCompleted ? (
                                    <CheckCircle className="mt-1 h-4 w-4 text-green-500" />
                                  ) : (
                                    <Circle className="mt-1 h-4 w-4 text-secondary-400" />
                                  )}
                                  <div>
                                    <p className="text-sm font-medium text-secondary-900">{resource.title}</p>
                                    <p className="text-xs text-secondary-500">
                                      {(resource.type ?? resource.mimeType ?? 'Resource').toString()} •{' '}
                                      {resourceCompleted ? 'Completed' : 'Not started'}
                                    </p>
                                  </div>
                                </div>
                                {resourceCompleted ? (
                                  <span className="inline-flex items-center gap-2 text-xs font-medium text-green-600">
                                    <CheckCircle className="h-4 w-4" />
                                    Tracked
                                  </span>
                                ) : (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    className="self-start border border-transparent px-3 py-1 text-xs font-medium text-primary-600 hover:border-primary-200 hover:bg-primary-50"
                                    disabled={resourceActionId === resource.id || recordResourceProgressMutation.isPending}
                                    onClick={() => handleResourceCompletion(resource)}
                                  >
                                    {resourceActionId === resource.id && recordResourceProgressMutation.isPending ? (
                                      <>
                                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                        Saving…
                                      </>
                                    ) : (
                                      <>
                                        <Play className="mr-2 h-3 w-3" />
                                        Mark video watched
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  )
}
